import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale';
import { HistoryType, HISTORY_TYPE_LABELS } from '../../types/history.types';
import { Customer, Mold } from '../../types';

interface HistoryFilterProps {
  // 기존
  selectedType: HistoryType | 'all';
  startDate: Date | null;
  endDate: Date | null;
  searchText: string;
  onTypeChange: (type: HistoryType | 'all') => void;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  onSearchChange: (text: string) => void;
  onReset: () => void;
  // 추가
  customers: Customer[];
  molds: Mold[];
  selectedCustomerId: string | 'all';
  selectedMoldId: string | 'all';
  onCustomerChange: (id: string | 'all') => void;
  onMoldChange: (id: string | 'all') => void;
}

const HistoryFilter: React.FC<HistoryFilterProps> = ({
  selectedType,
  startDate,
  endDate,
  searchText,
  onTypeChange,
  onStartDateChange,
  onEndDateChange,
  onSearchChange,
  onReset,
  customers,
  molds,
  selectedCustomerId,
  selectedMoldId,
  onCustomerChange,
  onMoldChange,
}) => {
  // 고객사 선택에 따라 금형 필터링
  const filteredMolds = selectedCustomerId === 'all' 
    ? molds 
    : molds.filter(m => m.customerId === selectedCustomerId);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
      <Box sx={{ mb: 3 }}>
        {/* 첫 번째 줄: 고객사, 금형 선택 */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          {/* 고객사 선택 */}
          <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: '48%', md: '250px' } }}>
            <FormControl fullWidth size="small">
              <InputLabel>고객사</InputLabel>
              <Select
                value={selectedCustomerId}
                label="고객사"
                onChange={(e) => onCustomerChange(e.target.value)}
              >
                <MenuItem value="all">전체</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* 금형 선택 */}
          <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: '48%', md: '250px' } }}>
            <FormControl fullWidth size="small">
              <InputLabel>금형</InputLabel>
              <Select
                value={selectedMoldId}
                label="금형"
                onChange={(e) => onMoldChange(e.target.value)}
              >
                <MenuItem value="all">전체</MenuItem>
                {filteredMolds.map((mold) => (
                  <MenuItem key={mold.id} value={mold.id}>
                    {mold.moldNumber} - {mold.moldName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* 두 번째 줄: 검색, 유형, 날짜 */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {/* 검색 */}
          <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '300px' } }}>
            <TextField
              fullWidth
              size="small"
              label="검색"
              placeholder="금형번호, 제목, 설명"
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </Box>

          {/* 유형 필터 */}
          <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: '48%', md: '180px' } }}>
            <FormControl fullWidth size="small">
              <InputLabel>유형</InputLabel>
              <Select
                value={selectedType}
                label="유형"
                onChange={(e) => onTypeChange(e.target.value as HistoryType | 'all')}
              >
                <MenuItem value="all">전체</MenuItem>
                {Object.entries(HISTORY_TYPE_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* 시작일 */}
          <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: '48%', md: '180px' } }}>
            <DatePicker
              label="시작일"
              value={startDate}
              onChange={onStartDateChange}
              slotProps={{
                textField: { size: 'small', fullWidth: true },
              }}
            />
          </Box>

          {/* 종료일 */}
          <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: '48%', md: '180px' } }}>
            <DatePicker
              label="종료일"
              value={endDate}
              onChange={onEndDateChange}
              slotProps={{
                textField: { size: 'small', fullWidth: true },
              }}
            />
          </Box>

          {/* 초기화 */}
          <Box sx={{ flex: '0 0 auto', minWidth: { xs: '100%', sm: '48%', md: '80px' } }}>
            <Chip
              label="초기화"
              onClick={onReset}
              clickable
              color="default"
              sx={{ height: 40 }}
            />
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default HistoryFilter;
